import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Check, ChevronLeft, ChevronRight, FileText, Home,
  ImagePlus, Loader2, Phone, Upload, X,
} from "lucide-react";
import { base44 } from "@/api/base44Client";
import { getLeadAttribution } from "@/lib/leadAttribution";
import { isSavedLead, trackConversion, trackQuoteSuccess } from "@/lib/conversionEvents";
import { CUSTOMER_TYPES, validLeadContact } from "@/lib/leadValidation";
import { getLocation } from "@/data/locations";
import { GuaranteeStrip } from "@/components/sections/GuaranteeSection";

const AUDIENCES = [
  { label: "Privatkund", value: "Privatkund", icon: Home, text: "Villa, radhus eller fritidshus" },
  { label: "Organisation", value: "business", icon: Building2, text: "BRF, företag, offentlig beställare eller fastighetsägare" },
];

const BUSINESS_TYPES = ["BRF", "Företag", "Offentlig", "Fastighetsägare"];

const SERVICES = [
  { label: "Byta eller renovera tak", value: "Takbyte eller takrenovering" },
  { label: "Läckage eller skada", value: "Takreparation eller läckage" },
  { label: "Besiktning & rådgivning", value: "Takbesiktning" },
  { label: "Service & underhåll", value: "Takservice" },
  { label: "Plåt & takdetaljer", value: "Plåtslageri" },
  { label: "Takmålning", value: "Takmålning" },
  { label: "Serviceavtal", value: "Serviceavtal" },
  { label: "Snö & is", value: "Takskottning" },
  { label: "Jag är osäker", value: "Jag vet inte" },
];

const PROPERTY_TYPES = ["Villa", "Radhus", "Fritidshus", "Flerbostadshus", "Kommersiell fastighet", "Industri", "Annat"];
const TIME_PLANS = ["Så snart som möjligt", "Inom 3 månader", "Inom 6–12 månader", "Planerar framåt", "Vet inte ännu"];

export default function QuoteForm({ prefillService = "" }) {
  const descriptionId = useId();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const startedRef = useRef(false);
  const submitLock = useRef(false);
  const [step, setStep] = useState(1);
  const [audience, setAudience] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [data, setData] = useState({
    customer_type: "",
    service_type: prefillService || "",
    address: "", postal_code: "", city: "", property_type: "",
    time_plan: "", description: "",
    name: "", phone: "", email: "", contact_role: "", preferred_contact: "Telefon",
    service_agreement_need: false, consent: false, website: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const attribution = getLeadAttribution();
    const requestedCustomer = params.get("customer_type") || attribution.origin_customer_type;
    const customerType = CUSTOMER_TYPES.includes(requestedCustomer) ? requestedCustomer : "";
    const requestedService = params.get("service_type") || prefillService || attribution.origin_service;
    const serviceType = SERVICES.some((service) => service.value === requestedService) ? requestedService : "";
    if (customerType) setAudience(customerType === "Privatkund" ? "Privatkund" : "business");
    setData((d) => ({
      ...d,
      ...attribution,
      customer_type: customerType || d.customer_type,
      service_type: serviceType || d.service_type,
      city: d.city || getLocation(attribution.origin_location)?.name || "",
    }));
  }, [prefillService]);

  const startForm = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackConversion("quote_start", { form_name: "quote" });
    }
  };
  const update = (key, value) => {
    startForm();
    setData((current) => ({ ...current, [key]: value }));
  };

  const chooseAudience = (value) => {
    setAudience(value);
    update("customer_type", value === "Privatkund" ? "Privatkund" : "");
  };

  const handleFiles = (event) => {
    const selected = Array.from(event.target.files || []).slice(0, 6 - files.length);
    setFiles((current) => [...current, ...selected].slice(0, 6));
  };

  const canProceed = () => {
    if (step === 1) return Boolean(data.customer_type && data.service_type);
    if (step === 2) return Boolean(data.address || data.postal_code || data.city);
    return validLeadContact(data);
  };

  const goToStep = (nextStep) => {
    startForm();
    if (nextStep > step) trackConversion("quote_step_complete", { form_name: "quote", step });
    setError("");
    setStep(nextStep);
    requestAnimationFrame(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const submit = async () => {
    if (submitLock.current) return;
    if (!canProceed()) {
      trackConversion("quote_error", { form_name: "quote", error_type: "validation" });
      setError("Kontrollera namn, telefon och e-post och godkänn integritetspolicyn.");
      return;
    }

    submitLock.current = true;
    startForm();
    setSubmitting(true);
    setError("");
    let errorType = "upload";
    try {
      const fileUrls = [];
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`Filen ${file.name} är större än 10 MB.`);
        }
        const { file_url } = await base44.integrations.Core.UploadFile({ file });
        fileUrls.push(file_url);
      }

      errorType = "submit";
      const response = await base44.functions.invoke("submitLead", {
        ...data, ...getLeadAttribution(),
        name: data.name.trim(), phone: data.phone.trim(), email: data.email.trim(),
        file_urls: fileUrls,
      });
      const result = response?.data;
      if (result?.ok && result?.spam) { navigate("/tack/"); return; }
      if (!isSavedLead(result)) throw new Error("Lead was not accepted");
      trackQuoteSuccess(result, "quote");
      navigate("/tack/");
    } catch (err) {
      submitLock.current = false;
      trackConversion("quote_error", { form_name: "quote", error_type: errorType });
      setError(err?.message?.startsWith("Filen") ? err.message : "Något gick fel vid inskick. Ring 08-767 61 20 så hjälper vi dig vidare.");
      setSubmitting(false);
    }
  };

  return (
    <div ref={formRef} id="offert" className="scroll-mt-32">
      <div className="mb-7 grid grid-cols-3 gap-2" aria-label={`Steg ${step} av 3`}>
        {["Behov", "Fastighet", "Kontakt"].map((label, index) => {
          const number = index + 1;
          const active = number <= step;
          return (
            <div key={label}>
              <div className={`h-1 transition-colors ${active ? "bg-tisab" : "bg-midnight/10"}`} />
              <span className={`mt-2 block text-[10px] font-bold uppercase tracking-[.12em] ${active ? "text-midnight" : "text-foreground/35"}`}>
                0{number} · {label}
              </span>
            </div>
          );
        })}
      </div>

      <input type="text" name="website" value={data.website} onChange={(event) => update("website", event.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      {step === 1 && (
        <FormStep eyebrow="Steg 1 av 3" title="Vem hjälper vi – och med vad?" desc="Två val räcker för att TISAB ska kunna styra förfrågan rätt.">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3">
            {AUDIENCES.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => chooseAudience(item.value)}
                aria-pressed={audience === item.value}
                className={`group flex min-h-[104px] items-start gap-3 border p-4 text-left transition-all ${audience === item.value ? "border-tisab bg-tisab text-white shadow-[0_12px_30px_rgba(6,7,8,.12)]" : "border-midnight/12 bg-white hover:border-tisab"}`}
              >
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center border ${audience === item.value ? "border-white/35" : "border-midnight/10 bg-parchment text-tisab"}`}>
                  <item.icon className="h-5 w-5" />
                </span>
                <span>
                  <strong className="block font-heading text-lg font-extrabold">{item.label}</strong>
                  <span className={`mt-1 block text-xs leading-relaxed ${audience === item.value ? "text-white/72" : "text-foreground/55"}`}>{item.text}</span>
                </span>
              </button>
            ))}
          </div>

          {audience === "business" && (
            <div className="mt-4 border border-midnight/10 bg-parchment p-4">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[.14em] text-foreground/50">Välj roll</p>
              <div className="flex flex-wrap gap-2">
                {BUSINESS_TYPES.map((type) => (
                  <ChoiceChip key={type} selected={data.customer_type === type} onClick={() => update("customer_type", type)}>{type}</ChoiceChip>
                ))}
              </div>
            </div>
          )}

          <div className="mt-7">
            <p className="mb-3 text-sm font-bold text-midnight">Vad behöver taket?</p>
            <div className="grid grid-cols-2 gap-2">
              {SERVICES.map((service) => (
                <button
                  key={service.value}
                  type="button"
                  onClick={() => update("service_type", service.value)}
                  className={`flex min-h-[52px] items-center justify-between gap-2 border px-3 py-3 text-left text-xs font-semibold sm:px-4 sm:text-sm transition-colors ${data.service_type === service.value ? "border-tisab bg-tisab/5 text-midnight ring-1 ring-tisab" : "border-midnight/10 bg-white text-foreground/75 hover:border-tisab"}`}
                >
                  {service.label}
                  {data.service_type === service.value ? <Check className="h-4 w-4 text-tisab" /> : <ChevronRight className="h-4 w-4 text-foreground/30" />}
                </button>
              ))}
            </div>
          </div>
        </FormStep>
      )}

      {step === 2 && (
        <FormStep eyebrow="Steg 2 av 3" title="Var finns taket?" desc="En adress eller ort räcker. Resten är frivilligt men hjälper TISAB att förbereda första kontakten.">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Gatuadress" value={data.address} onChange={(value) => update("address", value)} placeholder="Ex. Storgatan 12" className="sm:col-span-2" />
            <Field label="Postnummer" value={data.postal_code} onChange={(value) => update("postal_code", value)} inputMode="numeric" />
            <Field label="Ort" value={data.city} onChange={(value) => update("city", value)} />
            <Select label="Fastighetstyp" value={data.property_type} onChange={(value) => update("property_type", value)} options={PROPERTY_TYPES} />
            <Select label="Önskad tidplan" value={data.time_plan} onChange={(value) => update("time_plan", value)} options={TIME_PLANS} />
          </div>

          <div className="mt-4">
            <label htmlFor={descriptionId} className="mb-1.5 block text-sm font-bold text-midnight">Kort beskrivning <span className="font-normal text-foreground/40">(valfritt)</span></label>
            <textarea
              id={descriptionId}
              value={data.description}
              onChange={(event) => update("description", event.target.value)}
              rows={4}
              placeholder="Vad ser du, vad planerar ni eller vad oroar dig?"
              className="w-full border border-input bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-tisab focus:ring-1 focus:ring-tisab"
            />
          </div>

          <label className="mt-4 flex cursor-pointer items-center gap-4 border border-dashed border-midnight/20 bg-white p-4 transition-colors hover:border-tisab">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-parchment text-tisab"><ImagePlus className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1">
              <strong className="block text-sm text-midnight">Lägg till bilder eller PDF</strong>
              <span className="mt-0.5 block text-xs text-foreground/45">Valfritt · upp till 6 filer · max 10 MB per fil</span>
            </span>
            <Upload className="h-4 w-4 text-foreground/35" />
            <input type="file" multiple accept=".jpg,.jpeg,.png,.heic,.pdf" onChange={handleFiles} className="hidden" />
          </label>

          {files.length > 0 && (
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`} className="flex items-center gap-2 border border-midnight/10 bg-white px-3 py-2 text-xs">
                  <FileText className="h-4 w-4 shrink-0 text-tisab" />
                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                  <button type="button" onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Ta bort ${file.name}`}><X className="h-4 w-4 text-foreground/40 hover:text-midnight" /></button>
                </li>
              ))}
            </ul>
          )}
        </FormStep>
      )}

      {step === 3 && (
        <FormStep eyebrow="Steg 3 av 3" title="Hur når vi dig?" desc="TISAB får ärendet samlat och kan följa upp med rätt frågor från start.">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Namn *" value={data.name} onChange={(value) => update("name", value)} autoComplete="name" />
            <Field label="Telefon *" value={data.phone} onChange={(value) => update("phone", value)} inputMode="tel" autoComplete="tel" />
            <Field label="E-post *" value={data.email} onChange={(value) => update("email", value)} type="email" autoComplete="email" className="sm:col-span-2" />
            {data.customer_type !== "Privatkund" && <Field label="Roll i organisationen" value={data.contact_role} onChange={(value) => update("contact_role", value)} placeholder="Ex. styrelseledamot eller förvaltare" className="sm:col-span-2" />}
          </div>

          <div className="mt-5">
            <p className="mb-2 text-sm font-bold text-midnight">Jag vill helst bli kontaktad via</p>
            <div className="flex flex-wrap gap-2">
              {["Telefon", "E-post", "SMS"].map((method) => (
                <ChoiceChip key={method} selected={data.preferred_contact === method} onClick={() => update("preferred_contact", method)}>{method}</ChoiceChip>
              ))}
            </div>
          </div>

          {data.customer_type !== "Privatkund" && (
            <label className="mt-5 flex items-start gap-3 border border-midnight/10 bg-white p-4 text-sm text-foreground/72">
              <input type="checkbox" checked={data.service_agreement_need} onChange={(event) => update("service_agreement_need", event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--tisab-green))]" />
              Jag vill även prata om serviceavtal och långsiktigt underhåll.
            </label>
          )}

          <div className="mt-5 border border-midnight/10 bg-parchment p-4">
            <p className="text-[10px] font-bold uppercase tracking-[.14em] text-tisab">Din förfrågan</p>
            <p className="mt-2 text-sm font-semibold text-midnight">{data.customer_type} · {data.service_type}</p>
            <p className="mt-1 text-xs text-foreground/50">{[data.address, data.postal_code, data.city].filter(Boolean).join(", ")}{files.length ? ` · ${files.length} filer` : ""}</p>
          </div>

          <label className="mt-5 flex items-start gap-3 text-sm text-foreground/75">
            <input type="checkbox" checked={data.consent} onChange={(event) => update("consent", event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--tisab-green))]" />
            <span>Jag godkänner att TISAB kontaktar mig och har läst <a href="/integritetspolicy/" className="font-semibold text-tisab underline">integritetspolicyn</a>.</span>
          </label>
        </FormStep>
      )}

      {error && <p className="mt-5 border border-destructive/20 bg-destructive/8 px-4 py-3 text-sm text-destructive">{error}</p>}

      {step === 3 && <GuaranteeStrip className="mt-5" />}

      <div className="mt-7 flex items-center justify-between gap-3">
        {step > 1 ? (
          <button type="button" onClick={() => goToStep(step - 1)} className="inline-flex min-h-12 items-center gap-2 px-2 text-sm font-bold text-foreground/55 hover:text-midnight">
            <ChevronLeft className="h-4 w-4" /> Tillbaka
          </button>
        ) : <span />}
        {step < 3 ? (
          <button type="button" onClick={() => canProceed() && goToStep(step + 1)} disabled={!canProceed()} className="btn-primary !min-h-[52px] disabled:cursor-not-allowed disabled:opacity-35">
            Fortsätt <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={submitting || !canProceed()} className="btn-primary !min-h-[52px] disabled:cursor-not-allowed disabled:opacity-35">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Skickar…</> : <><Phone className="h-4 w-4" /> Skicka förfrågan</>}
          </button>
        )}
      </div>
    </div>
  );
}

function FormStep({ eyebrow, title, desc, children }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[.16em] text-tisab">{eyebrow}</p>
      <h2 className="mt-2 font-heading text-3xl font-extrabold leading-tight text-midnight sm:text-4xl">{title}</h2>
      <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground/55">{desc}</p>
      <div className="mt-7">{children}</div>
    </div>
  );
}

function ChoiceChip({ selected, onClick, children }) {
  return (
    <button type="button" onClick={onClick} className={`min-h-10 border px-4 text-sm font-semibold transition-colors ${selected ? "border-tisab bg-tisab text-white" : "border-midnight/12 bg-white text-foreground/70 hover:border-tisab"}`}>
      {children}
    </button>
  );
}

function Field({ label, value, onChange, className = "", type = "text", placeholder = "", ...props }) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-bold text-midnight">{label}</label>
      <input id={id} type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className="w-full border border-input bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-tisab focus:ring-1 focus:ring-tisab" {...props} />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-bold text-midnight">{label}</label>
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)} className="w-full border border-input bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-tisab focus:ring-1 focus:ring-tisab">
        <option value="">Välj om du vet</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </div>
  );
}
